import { api } from '@xtnd/web/shared/api';
import { fileUtil } from '@xtnd/web/shared';

import {
  ArAttachmentDownloadResponse,
  ArAttemptAttachmentsResponse,
  ArMatter,
  ArMatterAttemptsResponse,
  ArMatterInvoiceResponse,
  ArMatterInvoicesResponse,
  ArMatterInvoiceSummaryFilesResponse,
  ArMatterInvoiceSummaryFileUploadResponse,
  ArMatterResponse,
  ArRegisterAttemptAttachmentResponse,
  ArRemoveAttemptAttachmentResponse,
  FileCategory,
  MarkDiscountOnlyPayload,
  MarkDiscountOnlyResponse,
  MarkToBeAppealedPayload,
  MarkToBeAppealedResponse,
  MarkWriteOffPayload,
  MarkWriteOffResponse,
  MatterAttempt,
  MatterInvoiceSummaryFile,
  RegisterAttemptAttachmentRequest,
} from '../types';
import type { ApiResponse } from '@repo/types';
import { get, isEmpty } from 'lodash';
import { arCollectionQueueApi } from './arCollectionsQueueApi';

class ArMatterApiService {
  private readonly baseResource = '/ar-collections/matters';

  /**
   * Get matter detail via ID
   */
  async getMatterById(
    matterId: string,
    clientId: string,
    daysPastDue: number | null | undefined,
    daysPastDueCap?: number | null
  ): Promise<ArMatterResponse> {
    const params = new URLSearchParams();
    if (daysPastDue !== undefined && daysPastDue !== null) {
      params.append('daysPastDue', daysPastDue.toString());
    }
    if (daysPastDueCap !== undefined && daysPastDueCap !== null) {
      params.append('daysPastDueCap', daysPastDueCap.toString());
    }
    const endpoint = `${this.baseResource}/${matterId}/clients/${clientId}${params.size > 0 ? `?${params.toString()}` : ''}`;
    return await api.get<ArMatterResponse>(endpoint);
  }

  async getMatterInvoices(
    matterId: string,
    clientId: string,
    daysPastDue: number | null | undefined,
    daysPastDueCap?: number | null
  ): Promise<ArMatterInvoicesResponse> {
    const params = new URLSearchParams();
    if (daysPastDue !== undefined && daysPastDue !== null) {
      params.append('daysPastDue', daysPastDue.toString());
    }
    if (daysPastDueCap !== undefined && daysPastDueCap !== null) {
      params.append('daysPastDueCap', daysPastDueCap.toString());
    }
    const endpoint = `${this.baseResource}/${matterId}/clients/${clientId}/invoices${params.size > 0 ? `?${params.toString()}` : ''}`;
    return await api.get<ArMatterInvoicesResponse>(endpoint);
  }

  async getMatterAttempts(matterId: string, clientId: string): Promise<ArMatterAttemptsResponse> {
    const endpoint = `${this.baseResource}/${matterId}/clients/${clientId}/attempts`;
    return await api.get<ArMatterAttemptsResponse>(endpoint);
  }

  async postMatterAttempt(
    matterId: string,
    clientId: string,
    payload: any
  ): Promise<ApiResponse<MatterAttempt>> {
    const endpoint = `${this.baseResource}/${matterId}/clients/${clientId}/attempts`;
    return await api.post<ApiResponse<MatterAttempt>>(endpoint, payload);
  }

  async getAttemptAttachments(
    matterId: string,
    clientId: string,
    sk: string
  ): Promise<ArAttemptAttachmentsResponse> {
    const endpoint = `${this.baseResource}/${matterId}/clients/${clientId}/attempts/${encodeURIComponent(sk)}/attachments`;
    return await api.get<ArAttemptAttachmentsResponse>(endpoint);
  }

  async registerAttemptAttachment(
    matterId: string,
    clientId: string,
    sk: string,
    body: RegisterAttemptAttachmentRequest
  ): Promise<ArRegisterAttemptAttachmentResponse> {
    const endpoint = `${this.baseResource}/${matterId}/clients/${clientId}/attempts/${encodeURIComponent(sk)}/attachments`;
    return await api.post<ArRegisterAttemptAttachmentResponse>(endpoint, body);
  }

  async removeAttemptAttachment(
    matterId: string,
    clientId: string,
    sk: string,
    key: string
  ): Promise<ArRemoveAttemptAttachmentResponse> {
    const endpoint = `${this.baseResource}/${matterId}/clients/${clientId}/attempts/${encodeURIComponent(sk)}/attachments?key=${encodeURIComponent(key)}`;
    return await api.delete<ArRemoveAttemptAttachmentResponse>(endpoint);
  }

  async getAttemptAttachmentDownloadUrl(
    matterId: string,
    clientId: string,
    sk: string,
    key: string
  ): Promise<ArAttachmentDownloadResponse> {
    const endpoint = `${this.baseResource}/${matterId}/clients/${clientId}/attempts/${encodeURIComponent(sk)}/attachments/download?key=${encodeURIComponent(key)}`;
    return await api.get<ArAttachmentDownloadResponse>(endpoint);
  }

  async updateMatterInvoiceCarrierInPreview(payload: {
    invoiceId: string;
    isCarrierInPreview: boolean;
  }): Promise<ArMatterInvoiceResponse> {
    const { invoiceId, isCarrierInPreview } = payload;
    const endpoint = `/ar-collections/invoices/${invoiceId}/carrier-in-preview`;
    await new Promise(resolve => setTimeout(resolve, 1500));

    return await api.patch<ArMatterInvoiceResponse>(endpoint, { isCarrierInPreview });
  }

  async getMatterInvoiceSummaryFiles(
    invoiceId: string
  ): Promise<ArMatterInvoiceSummaryFilesResponse> {
    const endpoint = `/ar-collections/invoices/${invoiceId}/summary-files`;

    return await api.get<ArMatterInvoiceSummaryFilesResponse>(endpoint);
  }

  /**
   * Submits a request to update the status of an invoice, marking it as
   * "to be appealed" via a PATCH API call.
   *
   * This method executes a PATCH request to the endpoint
   * `/ar-collections/invoices/{invoiceId}/to-be-appealed` and extracts
   * the relevant appeal-related fields from the API response data.
   * * @param {string} invoiceId - The ID of the invoice to be marked for appeal.
   * @param {MarkToBeAppealedPayload} payload - The data payload containing the
   * necessary information for the appeal (e.g., appealed amount, comments, files).
   * @returns {Promise<MarkToBeAppealedResponse>} A Promise that resolves with an object
   * containing the extracted and reformatted response details, including the invoice ID,
   * appealed amount, collected amount, comments, and file list.
   * @throws {Error} Throws an error if the underlying API call fails or returns an invalid response structure.
   */
  async markToBeAppealed(
    invoiceId: string,
    appealId: string,
    payload: MarkToBeAppealedPayload
  ): Promise<MarkToBeAppealedResponse> {
    const endpoint = `/ar-collections/invoices/${invoiceId}/appeal/${appealId}/to-be-appealed`;
    const response = await api.patch(endpoint, payload);
    const invoice = get(response, 'data', {});

    if (isEmpty(invoice)) {
      throw new Error('Invoice not found or does not exist.');
    }

    const data: MarkToBeAppealedResponse = {
      invoiceId: get(invoice, 'invoiceId', ''),
      remainingAmount: get(invoice, 'appeal.remainingAmount', 0),
      collectedAmount: get(invoice, 'appeal.collectedAmount', 0),
      comment: get(invoice, 'appeal.comment', ''),
      writeOffAmount: get(invoice, 'writeOffAmount', 0),
      actualBalance: get(invoice, 'actualBalance', 0),
    };
    return data;
  }

  async markWriteOff(
    invoiceId: string,
    payload: MarkWriteOffPayload
  ): Promise<MarkWriteOffResponse> {
    const endpoint = `/ar-collections/invoices/${invoiceId}/write-offs`;
    const response = await api.patch(endpoint, payload);
    const invoice = get(response, 'data', {});

    if (isEmpty(invoice)) {
      throw new Error('Invoice not found or does not exist.');
    }

    const data: MarkWriteOffResponse = {
      invoiceId: get(invoice, 'invoiceId', ''),
      comment: get(invoice, 'appeal.comment', ''),
      writeOffAmount: get(invoice, 'writeOffAmount', 0),
      actualBalance: get(invoice, 'actualBalance', 0),
    };
    return data;
  }

  async uploadMatterInvoiceSummaryFiles(
    invoiceId: string,
    payload: { files: File[]; source: string; category: FileCategory }
  ): Promise<{
    successFiles: MatterInvoiceSummaryFile[];
    errorFiles: File[];
    errorReasons: string[];
  }> {
    const endpoint = `/ar-collections/invoices/${invoiceId}/summary-files`;
    const successFiles = [];
    const errorFiles = [];
    const errorReasons: string[] = [];
    for (const file of payload.files) {
      try {
        const body = {
          name: file.name,
          size: file.size,
          type: file.type,
          category: payload.category,
        };
        const { data } = await arCollectionQueueApi.getSummaryFileSignUrl(body);
        const storageResponse = await fetch(data.signUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });
        if (!storageResponse.ok) {
          throw new Error(`File storage rejected the upload (${storageResponse.status})`);
        }
        const response = await api.post<ArMatterInvoiceSummaryFileUploadResponse>(endpoint, {
          ...body,
          dataUrl: data.key,
          source: payload.source,
          category: payload.category,
        });
        successFiles.push(response.data);
      } catch (error) {
        errorFiles.push(file);
        errorReasons.push(error instanceof Error ? error.message : String(error));
      }
    }

    return { successFiles, errorFiles, errorReasons };
  }

  async deleteMatterInvoiceSummaryFile(invoiceId: string, fileKey: string): Promise<void> {
    const endpoint = `/ar-collections/invoices/${invoiceId}/summary-files/${encodeURIComponent(fileKey)}`;
    return await api.delete<void>(endpoint);
  }

  /**
   * Generate a collated PDF for selected invoices, open it in a new browser tab,
   * and automatically trigger the print dialog.
   */
  async printInvoicesPdf(items: { invoiceId: string; writeOffId: string }[]): Promise<void> {
    const endpoint = '/ar-collections/invoices/generate-pdf';
    const response = await api.post<{ data: string }>(endpoint, { items });

    const blob = fileUtil.base64ToBlob(response.data, 'application/pdf');
    const url = URL.createObjectURL(blob);

    const printTab = window.open(url, '_blank');
    if (printTab) {
      printTab.onload = () => printTab.print();
    } else {
      URL.revokeObjectURL(url);
      throw new Error('POPUP_BLOCKED');
    }
  }

  async captureArMatterData(arMatterData: ArMatter): Promise<any> {
    const endpoint = `/ar-collections/capture/matters/${arMatterData?.matterId}`;
    return await api.post(endpoint, arMatterData);
  }
}

// Export singleton instance
export const arMatterApi = new ArMatterApiService();
